// client/src/pages/DeliveryPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  formatRestaurantPrice,
  normalizeCurrency,
  vkHref,
  maxMessengerHref,
} from '../utils/formatPrice';
import { ThemeProvider, useTheme } from '../context/ThemeContext';
import { ThemeToggleButton } from '../components/ThemeToggleButton';
import BrandFooter from '../components/BrandFooter';
import './MenuPage.css';

function DeliveryPageInner() {
  const { slug } = useParams();
  const [step, setStep] = useState('menu'); // menu | cart | form | status
  const [settings, setSettings] = useState(null);
  const [zones, setZones] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [orderForm, setOrderForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorTitle, setErrorTitle] = useState('Доставка недоступна');
  const [activeCategory, setActiveCategory] = useState(null);

  const { colors, isDark } = useTheme();
  const desktopFrameBg = isDark ? '#3a3a44' : '#c8c7cc';
  const wrap = (content) => (
    <div className="menu-page-viewport" style={{ backgroundColor: desktopFrameBg }}>
      <div
        className="menu-page-column"
        style={{
          backgroundColor: colors.bg,
          minHeight: '100vh',
          color: colors.text,
          fontFamily: '-apple-system, sans-serif',
          transition: 'background-color 0.3s, color 0.3s',
        }}
      >
        <ThemeToggleButton />
        {content}
        <BrandFooter linkColor={colors.text} />
      </div>
    </div>
  );

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/delivery/${slug}/settings`);
      setError(null);
      setErrorTitle('Доставка недоступна');
      setSettings(data.settings);
      setZones(data.zones || []);
      setRestaurant(data.restaurant);
    } catch (err) {
      const st = err.response?.status;
      const msg = err.response?.data?.error;
      const code = err.response?.data?.code;
      if (st === 404) {
        setErrorTitle('Ресторан не найден');
        setError(msg || `Нет ресторана со slug «${slug}». Проверь ссылку: /delivery/ваш-slug`);
      } else if (st === 403) {
        setErrorTitle('Доставка недоступна');
        setError(
          msg ||
            (code === 'DELIVERY_DISABLED'
              ? 'Выключено в админке.'
              : 'Нет настроек доставки — зайди в админку → «Доставка».')
        );
      } else {
        setErrorTitle('Не удалось загрузить доставку');
        setError(msg || err.message || 'Проверь сеть или зайди позже.');
      }
    }
  }, [slug]);

  const fetchMenu = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/delivery/${slug}/menu`);
      setMenu(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setError(null);
    setErrorTitle('Доставка недоступна');
    setLoading(true);
    fetchSettings();
    fetchMenu();
  }, [slug, fetchSettings, fetchMenu]);

  useEffect(() => {
    const savedId = localStorage.getItem(`delivery_order_${slug}`);
    if (savedId && savedId.length > 0) {
      setOrderId(savedId);
      setStep('status');
    }
  }, [slug]);

  // Вкладка: имя из БД + «Доставка»; URL /delivery/{slug} — по слагу (name и slug независимы)
  useEffect(() => {
    document.title = restaurant?.name ? `${restaurant.name} — Доставка` : 'Доставка';
  }, [restaurant?.name, slug]);

  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);
  const menuCurrency = normalizeCurrency(restaurant?.currency);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        return prev.map(i =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === itemId);
      if (existing?.quantity === 1) {
        return prev.filter(i => i.id !== itemId);
      }
      return prev.map(i =>
        i.id === itemId ? { ...i, quantity: i.quantity - 1 } : i
      );
    });
  };

  const getQuantity = (itemId) =>
    cart.find(i => i.id === itemId)?.quantity || 0;

  // Фильтр по категориям
  const categories = menu.map(c => ({ id: c.id, name: c.name_ru || c.name }));
  const filteredMenu = activeCategory
    ? menu.filter(c => c.id === activeCategory)
    : menu;

  if (loading) {
    return wrap(
      <div style={{ ...s.loading, color: colors.textSecondary }}>
        ⏳ Загружаем меню...
      </div>
    );
  }

  if (error) {
    return wrap(
      <div style={{ ...s.errorPage, color: colors.text, minHeight: '100vh' }}>
        <div style={{ fontSize: '64px', marginBottom: '16px' }}>🚫</div>
        <div style={s.errorTitle}>{errorTitle}</div>
        <div style={{ ...s.errorText, color: colors.textSecondary }}>{error}</div>
        {slug && (
          <div
            style={{
              ...s.errorText,
              marginTop: '16px',
              fontSize: '12px',
              opacity: 0.75,
              color: colors.textSecondary,
            }}
          >
            Запрошен адрес:{' '}
            <code
              style={{
                background: isDark ? colors.bgChip : '#f0f0f0',
                padding: '2px 6px',
                borderRadius: '4px',
                color: colors.text,
              }}
            >
              /delivery/{slug}
            </code>
          </div>
        )}
      </div>
    );
  }

  if (step === 'status' && orderId) {
    return wrap(
      <OrderStatus
        slug={slug}
        orderId={orderId}
        restaurant={restaurant}
        settings={settings}
        currency={menuCurrency}
        onBack={() => {
          localStorage.removeItem(`delivery_order_${slug}`);
          setStep('menu');
          setCart([]);
          setOrderId(null);
          setOrderForm(null);
        }}
      />
    );
  }

  if (step === 'form') {
    return wrap(
      <OrderForm
        slug={slug}
        cart={cart}
        cartTotal={cartTotal}
        settings={settings}
        zones={zones}
        restaurant={restaurant}
        currency={menuCurrency}
        savedForm={orderForm}
        onFormChange={setOrderForm}
        onBack={() => setStep('menu')}
        onSuccess={(id) => {
          localStorage.setItem(`delivery_order_${slug}`, id);
          setOrderId(id);
          setStep('status');
        }}
      />
    );
  }

  return wrap(
    <div className="menu-list" style={{ ...s.page, paddingBottom: '100px', backgroundColor: 'transparent' }}>
      {/* Шапка (как MenuPage, но с отличием — доставка) */}
      {restaurant && (
        <div>
          {restaurant.cover_url && (
            <div style={{ position: 'relative', height: '240px', overflow: 'hidden' }}>
              <img src={restaurant.cover_url} alt={restaurant.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
          )}

          <div style={{ padding: '0 16px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
              margin: restaurant.cover_url ? '16px 0 12px' : '20px 0 12px',
            }}>
              <h1 style={{
                fontSize: '24px', fontWeight: '700',
                color: colors.text, lineHeight: 1.1, margin: 0,
              }}>
                {restaurant.name}
              </h1>
              <span style={{
                display: 'inline-block',
                backgroundColor: isDark ? 'rgba(46,125,50,0.25)' : '#E8F5E9', color: isDark ? '#A5D6A7' : '#2E7D32',
                padding: '6px 14px', borderRadius: '100px',
                fontSize: '14px', fontWeight: '600',
              }}>🚕 🛍️ Доставка</span>
            </div>

            {/* Инфо-карточка: время/мин.заказ + адрес/телефон/соцсети */}
            <div style={{
              backgroundColor: colors.bgCard,
              borderRadius: '18px', padding: '16px 18px',
              marginBottom: '14px', display: 'flex',
              flexDirection: 'column', gap: '16px',
              boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              {(settings?.delivery_time_min > 0 || settings?.min_order_amount > 0) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  {settings?.delivery_time_min > 0 && (
                    <span style={{ fontSize: '14px', color: colors.textSecondary }}>
                      ⏱ ~{settings.delivery_time_min} мин
                    </span>
                  )}
                  {settings?.min_order_amount > 0 && (
                    <span style={{ fontSize: '14px', color: colors.textSecondary }}>
                      Мин. заказ: {formatRestaurantPrice(settings.min_order_amount, menuCurrency)}
                    </span>
                  )}
                </div>
              )}

              {String(restaurant.description || '').trim() !== '' && (
                <div
                  style={{
                    fontSize: '14px',
                    color: colors.textSecondary,
                    lineHeight: 1.45,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {String(restaurant.description).trim()}
                </div>
              )}

              {restaurant.address && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0, marginTop: '2px' }}>📍</span>
                  <span style={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.4, flex: 1 }}>
                    {restaurant.address}
                  </span>
                </div>
              )}

              {restaurant.phone && (
                <a href={`tel:${restaurant.phone}`}
                  style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '20px', flexShrink: 0 }}>📞</span>
                  <span style={{ fontSize: '14px', color: colors.textSecondary }}>{restaurant.phone}</span>
                </a>
              )}

              {(restaurant.instagram || restaurant.telegram || restaurant.whatsapp
                || restaurant.vk_url || restaurant.max_messenger_url) && (
                <div style={{
                  paddingTop: '16px', marginTop: '2px', borderTop: `1px solid ${colors.border}`,
                  display: 'flex', flexDirection: 'row', gap: '10px', flexWrap: 'wrap',
                  alignItems: 'center',
                }}>
                  {restaurant.instagram && (
                    <a href={`https://instagram.com/${restaurant.instagram.replace('@', '')}`}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <defs>
                          <linearGradient id="ig-del" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#F9CE34"/>
                            <stop offset="33%" stopColor="#EE2A7B"/>
                            <stop offset="66%" stopColor="#6228D7"/>
                          </linearGradient>
                        </defs>
                        <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#ig-del)"/>
                        <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8" fill="none"/>
                        <circle cx="17.5" cy="6.5" r="1.2" fill="white"/>
                      </svg>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                        {restaurant.instagram}
                      </span>
                    </a>
                  )}
                  {restaurant.telegram && (
                    <a href={`https://t.me/${restaurant.telegram.replace('@', '')}`}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" fill="#29B6F6"/>
                        <path d="M5 11.5l4.5 1.8 1.8 4.7 1.7-3 3.5-7.5L5 11.5z" fill="white"/>
                      </svg>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                        {restaurant.telegram}
                      </span>
                    </a>
                  )}
                  {restaurant.whatsapp && (
                    <a href={`https://wa.me/${restaurant.whatsapp.replace(/\D/g, '')}`}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" fill="#25D366"/>
                        <path d="M17 14.9c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.6.1-.2.2-.6.8-.8 1-.1.2-.3.2-.5.1-.7-.3-1.4-.7-2-1.2-.5-.5-1-1.1-1.4-1.7-.1-.2 0-.4.1-.5l.4-.5c.1-.1.2-.3.2-.4 0-.1 0-.3-.1-.4L9.9 9c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3C7.9 9.1 7.5 9.6 7.5 10.6c0 1 .7 2 .8 2.1.1.1 1.4 2.2 3.4 3 1.9.8 1.9.5 2.3.5.4 0 1.2-.5 1.4-1 .2-.5.2-.9.1-1-.1-.1-.2-.1-.5-.2z" fill="white"/>
                      </svg>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                        {restaurant.whatsapp}
                      </span>
                    </a>
                  )}
                  {restaurant.vk_url && vkHref(restaurant.vk_url) && (
                    <a href={vkHref(restaurant.vk_url)}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                        <rect width="24" height="24" rx="4" fill="#0077FF"/>
                        <path d="M12.65 16.5c-5.2 0-8.18-3.57-8.32-9.5h2.58c.11 4.13 1.9 5.88 3.35 6.24V7h2.43v3.57c1.45-.16 2.93-1.8 3.44-3.57h2.43c-.39 2.45-2.05 4.1-3.22 4.66 1.17.54 2.72 2.1 3.35 4.84h-2.43c-.5-1.55-1.66-2.75-3.57-2.9V16.5h-2.9z" fill="#fff"/>
                      </svg>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>VK</span>
                    </a>
                  )}
                  {restaurant.max_messenger_url && maxMessengerHref(restaurant.max_messenger_url) && (
                    <a href={maxMessengerHref(restaurant.max_messenger_url)}
                      target="_blank" rel="noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 16px', borderRadius: '100px',
                        backgroundColor: colors.bgChip, textDecoration: 'none',
                        flex: 1, minWidth: '120px', justifyContent: 'center',
                      }}>
                      <span style={{
                        fontSize: '11px', fontWeight: '800', color: '#fff',
                        background: 'linear-gradient(135deg,#6C5CE7,#A29BFE)',
                        width: '22px', height: '22px', borderRadius: '6px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>M</span>
                      <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>MAX</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Фильтр категорий */}
      {categories.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          padding: '12px 16px',
          borderBottom: `1px solid ${colors.border}`,
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}>
          <button
            onClick={() => setActiveCategory(null)}
            type="button"
            style={{
              padding: '6px 16px',
              borderRadius: '20px',
              border: 'none',
              backgroundColor: activeCategory === null ? colors.bgChipActive : colors.bgChip,
              color: activeCategory === null ? (isDark ? colors.bg : '#fff') : colors.text,
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontFamily: 'inherit',
            }}
          >
            меню
          </button>
          {categories.map(cat => (
            <button
              type="button"
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                padding: '6px 16px',
                borderRadius: '20px',
                border: 'none',
                backgroundColor: activeCategory === cat.id ? colors.bgChipActive : colors.bgChip,
                color: activeCategory === cat.id ? (isDark ? colors.bg : '#fff') : colors.text,
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontFamily: 'inherit',
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Меню */}
      <div style={s.menuWrap}>
        {filteredMenu.length === 0 ? (
          <div style={{ ...s.empty, color: colors.textSecondary }}>
            <div style={{ fontSize: '48px' }}>🍽️</div>
            <div>Меню пока не добавлено</div>
          </div>
        ) : (
          filteredMenu.map(category => (
            <div key={category.id} style={s.category}>
              <div style={{ ...s.categoryTitle, color: colors.text }}>{category.name_ru || category.name}</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
                padding: '0 16px',
              }}>
                {category.items.map(item => {
                  const qty = getQuantity(item.id);
                  return (
                    <DeliveryCard
                      key={item.id}
                      item={item}
                      quantity={qty}
                      currency={menuCurrency}
                      onAdd={addToCart}
                      onRemove={removeFromCart}
                    />
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {cartCount > 0 &&
        createPortal(
          <div className="delivery-cart-bar" style={s.cartBar}>
            {settings?.min_order_amount > 0 && cartTotal < settings.min_order_amount && (
              <div style={s.minOrderWarn}>
                ⚠️ Минимальный заказ: {formatRestaurantPrice(settings.min_order_amount, menuCurrency)}
                · не хватает {formatRestaurantPrice(settings.min_order_amount - cartTotal, menuCurrency)}
              </div>
            )}
            <button
              type="button"
              style={{
                ...s.cartBtn,
                backgroundColor: isDark ? colors.bgChipActive : '#000',
                color: isDark ? colors.bg : '#fff',
                opacity: (settings?.min_order_amount > 0 && cartTotal < settings.min_order_amount) ? 0.5 : 1,
              }}
              onClick={() => {
                if (settings?.min_order_amount > 0 && cartTotal < settings.min_order_amount) return;
                setStep('form');
              }}
            >
              <span style={{
                ...s.cartCount,
                backgroundColor: isDark ? colors.bg : '#fff',
                color: isDark ? colors.text : '#000',
              }}>{cartCount}</span>
              <span>Оформить заказ</span>
              <span style={s.cartTotal}>{formatRestaurantPrice(cartTotal, menuCurrency)}</span>
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ─── Карточка блюда (grid-стиль как MenuCard) ─────────────────
function DeliveryCard({ item, quantity, onAdd, onRemove, currency = 'UZS' }) {
  const { colors, isDark } = useTheme();
  const name = item.name_ru || item.name;
  const price = formatRestaurantPrice(item.price, currency);

  return (
    <div style={{
      backgroundColor: colors.bgCard,
      borderRadius: '18px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
      position: 'relative',
    }}>
      {/* Изображение */}
      <div style={{
        width: '100%',
        paddingTop: '75%',
        position: 'relative',
        backgroundColor: colors.bgChip,
        overflow: 'hidden',
      }}>
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={name}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '40px',
          }}>🍽️</div>
        )}
        {quantity > 0 && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: '#3081f6',
            color: '#fff',
            fontSize: '11px',
            fontWeight: '600',
            padding: '4px 10px',
            borderRadius: '100px',
            backdropFilter: 'blur(8px)',
          }}>
            {quantity}
          </div>
        )}
      </div>

      {/* Тело карточки */}
      <div style={{
        padding: '12px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '500',
          color: colors.text,
          lineHeight: '1.3',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          flex: 1,
        }}>
          {name}
        </div>
        <div style={{
          fontSize: '13px',
          fontWeight: '600',
          color: colors.textSecondary,
        }}>
          {price}
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '6px',
        }}>
          <span style={{ fontSize: '11px', color: colors.textMuted }}>{item.weight || ''}</span>
          {quantity === 0 ? (
            <button
              type="button"
              onClick={() => onAdd(item)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '14px',
                backgroundColor: colors.accent,
                border: 'none',
                color: '#fff',
                fontSize: '22px',
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <span style={{ display: 'block', lineHeight: 1 }}>+</span>
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                style={{
                  width: '28px', height: '28px',
                  borderRadius: '10px',
                  backgroundColor: colors.bgChip,
                  border: 'none', fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0, color: colors.text,
                }}
              >−</button>
              <span style={{ fontSize: '15px', fontWeight: '700', minWidth: '16px', textAlign: 'center' }}>
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => onAdd(item)}
                style={{
                  width: '28px', height: '28px',
                  borderRadius: '10px',
                  backgroundColor: colors.accent,
                  border: 'none', fontSize: '18px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0, color: '#fff',
                }}
              >+</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Компонент телефона с маской ──────────────────────────────
function PhoneInput({ value, onChange, error }) {
  const digits = (value || '').replace('+998', '').replace(/\D/g, '').slice(0, 9);

  const handleChange = (e) => {
    const raw = e.target.value;
    const clean = raw.replace(/\D/g, '').slice(0, 9);
    onChange('+998' + clean);
  };

  const formatted = digits
    .replace(/(\d{2})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4')
    .replace(/(\d{2})(\d{3})(\d{2})/, '$1 $2 $3')
    .replace(/(\d{2})(\d{3})/, '$1 $2')
    .replace(/(\d{2})/, '$1');

  const isValid = digits.length === 9;
  const isEmpty = digits.length === 0;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        display: 'flex',
        border: `1.5px solid ${error ? '#E85D4A' : isValid ? '#4CAF50' : '#E0E0E0'}`,
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#FAFAFA',
        transition: 'border-color 0.2s',
      }}>
        <div style={{
          padding: '12px 10px 12px 14px',
          fontSize: '15px',
          color: '#333',
          fontWeight: '600',
          backgroundColor: '#F0F0F0',
          borderRight: '1px solid #E0E0E0',
          whiteSpace: 'nowrap',
          userSelect: 'none',
        }}>
          🇺🇿 +998
        </div>
        <input
          type="tel"
          inputMode="numeric"
          style={{
            flex: 1,
            padding: '12px 14px',
            border: 'none',
            outline: 'none',
            fontSize: '15px',
            backgroundColor: 'transparent',
            fontFamily: 'inherit',
            letterSpacing: '1px',
          }}
          placeholder="90 123 45 67"
          value={formatted}
          onChange={handleChange}
        />
        {!isEmpty && (
          <div style={{
            padding: '12px',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
          }}>
            {isValid ? '✅' : '⚠️'}
          </div>
        )}
      </div>
      {!isEmpty && !isValid && (
        <div style={{ marginTop: '4px' }}>
          <div style={{
            height: '3px',
            borderRadius: '2px',
            backgroundColor: '#F0F0F0',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${(digits.length / 9) * 100}%`,
              backgroundColor: digits.length > 6 ? '#FF9800' : '#F44336',
              transition: 'width 0.2s',
            }} />
          </div>
          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
            Введено {digits.length} из 9 цифр
          </div>
        </div>
      )}
      {error && (
        <div style={{ fontSize: '12px', color: '#E85D4A', marginTop: '4px' }}>
          {error}
        </div>
      )}
    </div>
  );
}

function OrderForm({
  slug, cart, cartTotal, settings, zones, restaurant, currency = 'UZS',
  savedForm, onFormChange, onBack, onSuccess,
}) {
  const defaultForm = {
    order_type: settings?.delivery_enabled ? 'delivery' : 'pickup',
    client_name: '',
    client_phone: '+998',
    street: '',
    house: '',
    building: '',
    entrance: '',
    floor: '',
    apartment: '',
    zone_id: '',
    delivery_time: 'now',
    scheduled_at: '',
    comment: '',
    payment_method: settings?.payment_cash ? 'cash' :
                    settings?.payment_click ? 'click' :
                    settings?.payment_payme ? 'payme' : 'cash',
  };

  const merged = savedForm ? { ...defaultForm, ...savedForm } : defaultForm;
  if (merged.address && !merged.street) merged.street = merged.address;
  if (!merged.client_phone || merged.client_phone === '') merged.client_phone = '+998';

  const [form, setFormState] = useState(merged);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const setForm = (newForm) => {
    setFormState(newForm);
    onFormChange?.(newForm);
    setErrors({});
  };

  const selectedZone = zones.find(z => String(z.id) === String(form.zone_id));
  const deliveryPrice = form.order_type === 'delivery' && selectedZone
    ? Number(selectedZone.price) : 0;
  const total = cartTotal + deliveryPrice;
  const isBelowMinOrder = form.order_type === 'delivery' &&
    selectedZone?.min_order > 0 &&
    cartTotal < selectedZone.min_order;

  const { colors, isDark } = useTheme();
  const f = {
    page: { ...s.page, maxWidth: '100%', width: '100%', backgroundColor: 'transparent', color: colors.text, minHeight: '100%', paddingBottom: '100px' },
    formHeader: { ...s.formHeader, backgroundColor: colors.bgCard, borderBottom: `1px solid ${colors.border}` },
    formTitle: { ...s.formTitle, color: colors.text },
    section: { ...s.section, backgroundColor: colors.bgCard, color: colors.text, borderBottom: `1px solid ${colors.border}` },
    sectionTitle: { ...s.sectionTitle, color: colors.text },
    label: { ...s.label, color: colors.textSecondary },
    input: { ...s.input, backgroundColor: isDark ? colors.bgChip : '#FAFAFA', color: colors.text, borderColor: colors.border },
    backBtn: { ...s.backBtn, backgroundColor: colors.bgCard, color: colors.text, border: `1px solid ${colors.border}` },
    typeBtn: { ...s.typeBtn, backgroundColor: colors.bgChip, color: colors.text, border: `1px solid ${colors.border}` },
    backBtnFull: { ...s.backBtnFull, backgroundColor: colors.bgCard, color: colors.text, border: `1px solid ${colors.border}` },
    cartItem: { ...s.cartItem, color: colors.text, borderBottom: `1px solid ${colors.border}` },
    cartItemTotal: { ...s.cartItemTotal, color: colors.text },
  };
  const errorMsgT = { ...s.errorMsg, color: isDark ? '#ffab91' : '#E85D4A' };
  const submitT = {
    ...s.submitBtn,
    backgroundColor: isDark ? colors.bgChipActive : '#000',
    color: isDark ? colors.bg : '#fff',
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.client_name.trim() || form.client_name.trim().length < 2) {
      newErrors.client_name = 'Введите имя (минимум 2 символа)';
    }

    const phoneDigits = (form.client_phone || '').replace('+998', '').replace(/\D/g, '');
    if (phoneDigits.length !== 9) {
      newErrors.client_phone = 'Введите 9 цифр после +998';
    }

    if (form.order_type === 'delivery') {
      if (!form.street?.trim() || form.street.trim().length < 3) {
        newErrors.street = 'Введите название улицы';
      }
      if (!form.house?.trim()) {
        newErrors.house = 'Укажите номер дома';
      }
      if (zones.length > 0 && !form.zone_id) {
        newErrors.zone_id = 'Выберите район доставки';
      }
    }

    if (form.delivery_time === 'scheduled' && !form.scheduled_at) {
      newErrors.scheduled_at = 'Укажите время доставки';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTimeout(() => {
        document.querySelector('[data-error="true"]')?.scrollIntoView({
          behavior: 'smooth', block: 'center',
        });
      }, 100);
      return;
    }

    const address = form.order_type === 'delivery' ? [
      form.street,
      form.house && `д. ${form.house}`,
      form.building && `корп. ${form.building}`,
      form.entrance && `подъезд ${form.entrance}`,
      form.floor && `этаж ${form.floor}`,
      form.apartment && `кв. ${form.apartment}`,
    ].filter(Boolean).join(', ') : null;

    if (selectedZone?.min_order > 0 && cartTotal < selectedZone.min_order) {
      setSubmitError(`Минимальный заказ для «${selectedZone.name}»: ${formatRestaurantPrice(selectedZone.min_order, currency)}`);
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const { data } = await axios.post(`/api/delivery/${slug}/order`, {
        order_type: form.order_type,
        client_name: form.client_name.trim(),
        client_phone: form.client_phone,
        address,
        zone_id: form.zone_id || null,
        delivery_time: form.delivery_time,
        scheduled_at: form.delivery_time === 'scheduled' && form.scheduled_at
          ? new Date(form.scheduled_at).toISOString() : null,
        comment: form.comment || '',
        payment_method: form.payment_method,
        items: cart.map(i => ({
          menu_item_id: i.id,
          name: i.name_ru || i.name,
          price: i.price,
          quantity: i.quantity,
        })),
      });
      onSuccess(data.order_id);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Ошибка при оформлении заказа');
    } finally {
      setSubmitting(false);
    }
  };

  const paymentMethods = [
    settings?.payment_cash && { key: 'cash', label: '💵 Наличные' },
    settings?.payment_click && { key: 'click', label: '📱 Click' },
    settings?.payment_payme && { key: 'payme', label: '💳 Payme' },
  ].filter(Boolean);

  return (
    <div style={f.page}>
      <div style={f.formHeader}>
        <button type="button" style={f.backBtn} onClick={onBack}>← Назад</button>
        <div style={f.formTitle}>Оформление заказа</div>
      </div>

      {settings?.delivery_enabled && settings?.pickup_enabled && (
        <div style={f.section}>
          <div style={f.sectionTitle}>Тип получения</div>
          <div style={s.typeToggle}>
            <button
              type="button"
              style={{
                ...f.typeBtn,
                ...(form.order_type === 'delivery' ? s.typeBtnActive : {}),
              }}
              onClick={() => setForm({ ...form, order_type: 'delivery' })}
            >
              🚗 Доставка
            </button>
            <button
              type="button"
              style={{
                ...f.typeBtn,
                ...(form.order_type === 'pickup' ? s.typeBtnActive : {}),
              }}
              onClick={() => setForm({ ...form, order_type: 'pickup' })}
            >
              🏃 Самовывоз
            </button>
          </div>
        </div>
      )}

      {form.order_type === 'delivery' && (
        <div style={f.section}>
          <div style={f.sectionTitle}>📍 Адрес доставки</div>

          {zones.length > 0 && (
            <div style={{ marginBottom: '10px' }} data-error={errors.zone_id ? 'true' : undefined}>
              <div style={f.label}>Район / Зона доставки *</div>
              <select
                style={{
                  ...f.input,
                  borderColor: errors.zone_id ? '#E85D4A' : colors.border,
                }}
                value={form.zone_id}
                onChange={e => setForm({ ...form, zone_id: e.target.value })}
              >
                <option value="">— Выберите район —</option>
                {zones.map(zone => (
                  <option key={zone.id} value={zone.id}>
                    {zone.name} — {formatRestaurantPrice(zone.price, currency)}
                    {zone.min_order > 0 && ` (мин. ${formatRestaurantPrice(zone.min_order, currency)})`}
                  </option>
                ))}
              </select>
              {errors.zone_id && (
                <div style={{ fontSize: '12px', color: '#E85D4A', marginTop: '4px' }}>
                  {errors.zone_id}
                </div>
              )}
            </div>
          )}

          <div style={{ marginBottom: '10px' }} data-error={errors.street ? 'true' : undefined}>
            <div style={f.label}>Улица *</div>
            <input
              style={{
                ...f.input,
                borderColor: errors.street ? '#E85D4A' : colors.border,
              }}
              placeholder="Например: ул. Амира Темура"
              value={form.street || ''}
              onChange={e => setForm({ ...form, street: e.target.value })}
            />
            {errors.street && (
              <div style={{ fontSize: '12px', color: '#E85D4A', marginTop: '4px' }}>
                {errors.street}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }} data-error={errors.house ? 'true' : undefined}>
              <div style={f.label}>Дом *</div>
              <input
                style={{
                  ...f.input,
                  borderColor: errors.house ? '#E85D4A' : colors.border,
                }}
                placeholder="12"
                value={form.house || ''}
                onChange={e => setForm({ ...form, house: e.target.value })}
              />
              {errors.house && (
                <div style={{ fontSize: '12px', color: '#E85D4A', marginTop: '4px' }}>
                  {errors.house}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={f.label}>Корпус</div>
              <input
                style={f.input}
                placeholder="A"
                value={form.building || ''}
                onChange={e => setForm({ ...form, building: e.target.value })}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <div>
              <div style={f.label}>Подъезд</div>
              <input
                style={f.input}
                placeholder="3"
                value={form.entrance || ''}
                onChange={e => setForm({ ...form, entrance: e.target.value })}
              />
            </div>
            <div>
              <div style={f.label}>Этаж</div>
              <input
                style={f.input}
                placeholder="5"
                value={form.floor || ''}
                onChange={e => setForm({ ...form, floor: e.target.value })}
              />
            </div>
            <div>
              <div style={f.label}>Квартира</div>
              <input
                style={f.input}
                placeholder="47"
                value={form.apartment || ''}
                onChange={e => setForm({ ...form, apartment: e.target.value })}
              />
            </div>
          </div>

          {(form.street || form.house) && (
            <div style={{
              marginTop: '10px',
              padding: '10px 12px',
              backgroundColor: isDark ? colors.bgChip : '#F0F8FF',
              borderRadius: '10px',
              fontSize: '13px',
              color: colors.text,
              lineHeight: '1.5',
            }}>
              📍 {[
                form.street,
                form.house && `д. ${form.house}`,
                form.building && `корп. ${form.building}`,
                form.entrance && `подъезд ${form.entrance}`,
                form.floor && `этаж ${form.floor}`,
                form.apartment && `кв. ${form.apartment}`,
              ].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      )}

      <div style={f.section}>
        <div style={f.sectionTitle}>👤 Ваши данные</div>

        <div style={{ marginBottom: '10px' }} data-error={errors.client_name ? 'true' : undefined}>
          <input
            style={{
              ...f.input,
              borderColor: errors.client_name ? '#E85D4A' : colors.border,
            }}
            placeholder="Ваше имя"
            value={form.client_name}
            onChange={e => setForm({ ...form, client_name: e.target.value })}
          />
          {errors.client_name && (
            <div style={{ fontSize: '12px', color: '#E85D4A', marginTop: '4px' }}>
              {errors.client_name}
            </div>
          )}
        </div>

        <div data-error={errors.client_phone ? 'true' : undefined}>
          <PhoneInput
            value={form.client_phone}
            onChange={val => setForm({ ...form, client_phone: val })}
            error={errors.client_phone}
          />
        </div>
      </div>

      <div style={f.section}>
        <div style={f.sectionTitle}>🕐 Время доставки</div>
        <div style={s.typeToggle}>
          <button
            type="button"
            style={{
              ...f.typeBtn,
              ...(form.delivery_time === 'now' ? s.typeBtnActive : {}),
            }}
            onClick={() => setForm({ ...form, delivery_time: 'now' })}
          >
            ⚡ Сейчас
          </button>
          <button
            type="button"
            style={{
              ...f.typeBtn,
              ...(form.delivery_time === 'scheduled' ? s.typeBtnActive : {}),
            }}
            onClick={() => setForm({ ...form, delivery_time: 'scheduled' })}
          >
            🕐 Ко времени
          </button>
        </div>
        {form.delivery_time === 'scheduled' && (
          <div style={{ marginTop: '10px' }} data-error={errors.scheduled_at ? 'true' : undefined}>
            <input
              type="datetime-local"
              style={{
                ...f.input,
                borderColor: errors.scheduled_at ? '#E85D4A' : colors.border,
              }}
              value={form.scheduled_at}
              onChange={e => setForm({ ...form, scheduled_at: e.target.value })}
            />
            {errors.scheduled_at && (
              <div style={{ fontSize: '12px', color: '#E85D4A', marginTop: '4px' }}>
                {errors.scheduled_at}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={f.section}>
        <div style={f.sectionTitle}>💬 Комментарий</div>
        <textarea
          style={{ ...f.input, height: '80px', resize: 'none' }}
          placeholder="Дополнительные пожелания..."
          value={form.comment}
          onChange={e => setForm({ ...form, comment: e.target.value })}
        />
      </div>

      {paymentMethods.length > 0 && (
        <div style={f.section}>
          <div style={f.sectionTitle}>💳 Способ оплаты</div>
          <div style={s.typeToggle}>
            {paymentMethods.map(m => (
              <button
                type="button"
                key={m.key}
                style={{
                  ...f.typeBtn,
                  ...(form.payment_method === m.key ? s.typeBtnActive : {}),
                }}
                onClick={() => setForm({ ...form, payment_method: m.key })}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={f.section}>
        <div style={f.sectionTitle}>🛒 Ваш заказ</div>
        {cart.map(item => (
          <div key={item.id} style={f.cartItem}>
            <span>{item.name_ru || item.name} × {item.quantity}</span>
            <span>{formatRestaurantPrice(item.price * item.quantity, currency)}</span>
          </div>
        ))}
        {deliveryPrice > 0 && (
          <div style={{ ...f.cartItem, color: colors.textSecondary }}>
            <span>Доставка ({selectedZone?.name})</span>
            <span>{formatRestaurantPrice(deliveryPrice, currency)}</span>
          </div>
        )}
        <div style={f.cartItemTotal}>
          <span>Итого</span>
          <span>{formatRestaurantPrice(total, currency)}</span>
        </div>
      </div>

      {form.order_type === 'delivery' && selectedZone?.min_order > 0 && cartTotal < selectedZone.min_order && (
        <div style={{
          margin: '0 16px 8px',
          padding: '10px 14px',
          borderRadius: '10px',
          backgroundColor: isDark ? 'rgba(230, 81, 0, 0.2)' : '#FFF3E0',
          color: isDark ? '#ffab40' : '#E65100',
          fontSize: '13px',
          textAlign: 'center',
        }}>
          ⚠️ Минимальный заказ для «{selectedZone.name}»: {formatRestaurantPrice(selectedZone.min_order, currency)}
          · добавьте ещё на {formatRestaurantPrice(selectedZone.min_order - cartTotal, currency)}
        </div>
      )}

      {submitError && <div style={errorMsgT}>{submitError}</div>}

      <button
        type="button"
        style={{
          ...submitT,
          opacity: (submitting || isBelowMinOrder) ? 0.6 : 1,
        }}
        onClick={handleSubmit}
        disabled={submitting || isBelowMinOrder}
      >
        {submitting ? '⏳ Оформляем...' : `Заказать · ${formatRestaurantPrice(total, currency)}`}
      </button>
    </div>
  );
}

function OrderStatus({ slug, orderId, restaurant, settings, currency = 'UZS', onBack }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { colors } = useTheme();

  const pageSt = { ...s.page, maxWidth: '100%', width: '100%', backgroundColor: 'transparent', color: colors.text, minHeight: '100%' };
  const sectionSt = { ...s.section, backgroundColor: colors.bgCard, color: colors.text, borderBottom: `1px solid ${colors.border}` };
  const sectionTitleSt = { ...s.sectionTitle, color: colors.text };
  const cartItemSt = { ...s.cartItem, color: colors.text, borderBottom: `1px solid ${colors.border}` };
  const cartItemTotalSt = { ...s.cartItemTotal, color: colors.text };
  const backBtnFullSt = { ...s.backBtnFull, backgroundColor: colors.bgCard, color: colors.text, border: `1px solid ${colors.border}` };

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/delivery/${slug}/order/${orderId}`);
      setOrder(data);
    } catch (err) {
      console.error(err);
      const st = err.response?.status;
      if (st === 404) {
        try {
          localStorage.removeItem(`delivery_order_${slug}`);
        } catch {
          /* ignore */
        }
      }
    } finally {
      setLoading(false);
    }
  }, [slug, orderId]);

  useEffect(() => {
    fetchOrder();
    const timer = setInterval(fetchOrder, 30000);
    return () => clearInterval(timer);
  }, [fetchOrder]);

  const STATUS_STEPS = [
    { key: 'new',        label: 'Принят',      icon: '✅' },
    { key: 'accepted',   label: 'Подтверждён', icon: '👍' },
    { key: 'cooking',    label: 'Готовится',   icon: '🍳' },
    { key: 'on_the_way', label: 'В пути',      icon: '🚗' },
    { key: 'delivered',  label: 'Доставлен',   icon: '🎉' },
  ];

  const STATUS_STEPS_PICKUP = [
    { key: 'new',      label: 'Принят',      icon: '✅' },
    { key: 'accepted', label: 'Подтверждён', icon: '👍' },
    { key: 'cooking',  label: 'Готовится',   icon: '🍳' },
    { key: 'ready',    label: 'Готово!',     icon: '📦' },
  ];

  if (loading) return <div style={{ ...s.loading, color: colors.textSecondary }}>⏳ Загрузка...</div>;
  if (!order) {
    return (
      <div style={{ ...s.errorPage, color: colors.text, padding: '24px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔍</div>
        <div style={{ fontWeight: '700', marginBottom: '8px' }}>Заказ не найден</div>
        <div style={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.5, marginBottom: '20px' }}>
          Ссылка устарела, заказ в другом ресторане или сохранён старый номер после смены слага. Оформите новый — мы сбросили привязку в браузере.
        </div>
        <button
          type="button"
          onClick={onBack}
          style={backBtnFullSt}
        >
          ← К меню доставки
        </button>
      </div>
    );
  }

  const steps = order.order_type === 'pickup' ? STATUS_STEPS_PICKUP : STATUS_STEPS;
  const currentStepIdx = steps.findIndex(st => st.key === order.status);
  const isDone = ['delivered', 'ready'].includes(order.status);
  const isCancelled = order.status === 'cancelled';

  return (
    <div style={pageSt}>
      <div style={ss.orderNumBlock}>
        <div style={ss.orderNumLabel}>Номер вашего заказа</div>
        <div style={ss.orderNum}>#{order.id}</div>
        <div style={ss.orderNumHint}>Назовите этот номер оператору</div>
      </div>

      <div style={ss.statusBlock}>
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>
          {isCancelled ? '❌' : isDone ? '🎉' : '🚚'}
        </div>
        <div style={ss.statusTitle}>
          {isCancelled ? 'Заказ отменён' :
           order.status === 'delivered' ? 'Заказ доставлен!' :
           order.status === 'ready' ? 'Заказ готов!' :
           'Заказ в работе'}
        </div>
        <div style={ss.statusTime}>
          Создан: {new Date(order.created_at).toLocaleString('ru')}
        </div>
      </div>

      {!isCancelled && (
        <div style={ss.stepsWrap}>
          {steps.map((step, idx) => {
            const done = idx <= currentStepIdx;
            const active = idx === currentStepIdx;
            return (
              <React.Fragment key={step.key}>
                <div style={ss.stepCol}>
                  <div style={{
                    ...ss.stepIcon,
                    backgroundColor: done ? '#000' : '#F0F0F0',
                    color: done ? '#fff' : '#ccc',
                    transform: active ? 'scale(1.15)' : 'scale(1)',
                    boxShadow: active ? '0 0 0 4px rgba(0,0,0,0.1)' : 'none',
                  }}>
                    {step.icon}
                  </div>
                  <div style={{
                    ...ss.stepLabel,
                    fontWeight: active ? '700' : '400',
                    color: done ? '#000' : '#bbb',
                  }}>
                    {step.label}
                  </div>
                </div>
                {idx < steps.length - 1 && (
                  <div style={{
                    ...ss.stepLine,
                    backgroundColor: idx < currentStepIdx ? '#000' : '#F0F0F0',
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {order.payment_status === 'paid' && (
        <div style={{
          margin: '0 16px 16px',
          padding: '12px 16px',
          borderRadius: '12px',
          backgroundColor: '#E8F5E9',
          border: '1px solid #A5D6A7',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '600',
          color: '#2E7D32',
        }}>
          ✅ Оплата получена
        </div>
      )}

      {order.payment_status === 'waiting_payment' && order.payment_link && (
        <div style={{
          margin: '0 16px 16px',
          padding: '16px',
          borderRadius: '16px',
          backgroundColor: '#FFF8E1',
          border: '1px solid #FFE082',
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '700',
            marginBottom: '4px',
            color: '#F57F17',
          }}>
            ⏳ Ожидаем оплату
          </div>
          <div style={{
            fontSize: '13px',
            color: '#888',
            marginBottom: '12px',
          }}>
            Заказ будет подтверждён после оплаты
          </div>
          <a
            href={order.payment_link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              padding: '14px',
              borderRadius: '12px',
              backgroundColor:
                order.payment_method === 'payme' ? '#00AAFF' : '#00C853',
              color: '#fff',
              textAlign: 'center',
              fontWeight: '700',
              fontSize: '15px',
              textDecoration: 'none',
            }}
          >
            {order.payment_method === 'payme'
              ? '💳 Оплатить через Payme'
              : '📱 Оплатить через Click'}
          </a>
        </div>
      )}

      {order.status === 'on_the_way' && order.courier_name && (
        <div style={ss.courierCard}>
          <div style={ss.courierCardHeader}>
            <div style={{ fontSize: '40px', marginBottom: '4px' }}>🚗</div>
            <div style={{ fontWeight: '800', fontSize: '16px' }}>
              Курьер уже едет к вам!
            </div>
          </div>
          <div style={ss.courierCardBody}>
            <div style={ss.courierCardRow}>
              <div style={ss.courierAvatar}>
                {order.courier_name?.charAt(0)?.toUpperCase() || '👤'}
              </div>
              <div>
                <div style={{ fontWeight: '700', fontSize: '17px' }}>
                  {order.courier_name}
                </div>
                <div style={{ fontSize: '13px', color: '#888' }}>
                  Ваш курьер
                </div>
              </div>
            </div>
            {order.courier_phone && (
              <a href={`tel:${order.courier_phone}`} style={ss.courierCallBtn}>
                📞 Позвонить курьеру
                <span style={{ fontWeight: '400', fontSize: '14px' }}>
                  {' '}{order.courier_phone}
                </span>
              </a>
            )}
            <div style={ss.courierHint}>
              Среднее время доставки 30–45 минут
            </div>
          </div>
        </div>
      )}

      {settings?.manager_phone && (
        <div style={ss.contactCard}>
          <div style={ss.contactTitle}>👨‍💼 Есть вопрос по заказу?</div>
          <a href={`tel:${settings.manager_phone}`} style={ss.callBtn}>
            📞 Позвонить менеджеру
          </a>
        </div>
      )}

      {settings?.phone && !settings?.manager_phone && (
        <div style={ss.contactCard}>
          <div style={ss.contactTitle}>📞 Контакт ресторана</div>
          <a href={`tel:${settings.phone}`} style={ss.callBtn}>
            Позвонить: {settings.phone}
          </a>
        </div>
      )}

      <div style={sectionSt}>
        <div style={sectionTitleSt}>🛒 Состав заказа</div>
        {(order.items || []).map((item, i) => (
          <div key={i} style={cartItemSt}>
            <span>{item.name} × {item.quantity}</span>
            <span>{formatRestaurantPrice(item.total, currency)}</span>
          </div>
        ))}
        {order.delivery_price > 0 && (
          <div style={{ ...cartItemSt, color: colors.textSecondary }}>
            <span>Доставка</span>
            <span>{formatRestaurantPrice(order.delivery_price, currency)}</span>
          </div>
        )}
        <div style={cartItemTotalSt}>
          <span>Итого</span>
          <span>{formatRestaurantPrice(order.total, currency)}</span>
        </div>
      </div>

      <div style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '12px', padding: '8px' }}>
        Статус обновляется каждые 30 секунд
      </div>

      <button type="button" style={backBtnFullSt} onClick={onBack}>
        ← Новый заказ
      </button>
    </div>
  );
}

const ss = {
  orderNumBlock: {
    backgroundColor: '#000', color: '#fff',
    padding: '24px 16px', textAlign: 'center',
  },
  orderNumLabel: {
    fontSize: '13px', opacity: 0.7,
    textTransform: 'uppercase', letterSpacing: '1px',
    marginBottom: '8px',
  },
  orderNum: {
    fontSize: '64px', fontWeight: '900',
    lineHeight: 1, marginBottom: '8px',
  },
  orderNumHint: {
    fontSize: '12px', opacity: 0.6,
  },
  statusBlock: {
    backgroundColor: '#fff', padding: '24px 16px',
    textAlign: 'center', marginBottom: '8px',
    borderBottom: '1px solid #F0F0F0',
  },
  statusTitle: { fontSize: '22px', fontWeight: '800', marginBottom: '4px' },
  statusTime: { fontSize: '12px', color: '#bbb' },
  stepsWrap: {
    backgroundColor: '#fff', padding: '20px 16px',
    marginBottom: '8px', display: 'flex',
    alignItems: 'center',
  },
  stepCol: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', flex: 1,
  },
  stepIcon: {
    width: '36px', height: '36px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', marginBottom: '6px',
    transition: 'all 0.3s',
    zIndex: 1, position: 'relative',
  },
  stepLine: {
    height: '2px', flex: 1,
    marginBottom: '22px',
    minWidth: '8px',
  },
  stepLabel: {
    fontSize: '10px', textAlign: 'center',
    lineHeight: '1.2', maxWidth: '52px',
  },
  contactCard: {
    backgroundColor: '#fff', padding: '16px',
    marginBottom: '8px', textAlign: 'center',
  },
  contactTitle: { fontSize: '13px', color: '#888', marginBottom: '10px' },
  contactName: { fontSize: '17px', fontWeight: '700', marginBottom: '10px' },
  callBtn: {
    display: 'inline-block', padding: '10px 24px',
    borderRadius: '20px', backgroundColor: '#E8F5E9',
    color: '#2E7D32', fontSize: '15px', fontWeight: '600',
    textDecoration: 'none',
  },
  courierCard: {
    margin: '0 16px 16px',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  courierCardHeader: {
    background: 'linear-gradient(135deg, #000 0%, #333 100%)',
    padding: '16px',
    color: '#fff',
    textAlign: 'center',
  },
  courierCardBody: {
    backgroundColor: '#fff',
    padding: '16px',
  },
  courierCardRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  courierAvatar: {
    width: '48px', height: '48px',
    borderRadius: '50%',
    backgroundColor: '#F0F0F0',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    flexShrink: 0,
  },
  courierCallBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px',
    borderRadius: '12px',
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    fontWeight: '700',
    fontSize: '16px',
    textDecoration: 'none',
    border: '1px solid #A5D6A7',
  },
  courierHint: {
    marginTop: '10px',
    fontSize: '12px',
    color: '#999',
    textAlign: 'center',
  },
};

const s = {
  page: {
    width: '100%',
    maxWidth: '100%',
    margin: 0,
    padding: '0 0 calc(100px + env(safe-area-inset-bottom, 0px)) 0',
    fontFamily: '-apple-system, sans-serif',
    minHeight: '100%',
  },
  loading: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#888',
    fontSize: '16px',
    fontFamily: '-apple-system, sans-serif',
  },
  errorPage: {
    padding: '80px 24px',
    textAlign: 'center',
    fontFamily: '-apple-system, sans-serif',
  },
  errorTitle: { fontSize: '20px', fontWeight: '700', marginBottom: '8px' },
  errorText: { fontSize: '14px', color: '#888' },
  header: {
    backgroundColor: '#fff',
    padding: '20px 16px 16px',
    borderBottom: '1px solid #F0F0F0',
    marginBottom: '8px',
  },
  restaurantName: { fontSize: '22px', fontWeight: '800', marginBottom: '4px' },
  deliveryBadge: {
    display: 'inline-block',
    backgroundColor: '#E8F5E9', color: '#2E7D32',
    padding: '2px 10px', borderRadius: '20px',
    fontSize: '12px', fontWeight: '600', marginBottom: '4px',
  },
  deliveryTime: { fontSize: '13px', color: '#666', marginTop: '2px' },
  minOrder: { fontSize: '13px', color: '#888', marginTop: '2px' },
  menuWrap: { padding: '0 0 16px 0' },
  category: { marginBottom: '16px' },
  categoryTitle: {
    fontSize: '22px', fontWeight: '800',
    padding: '20px 16px 12px',
    color: '#000',
  },
  cartBar: {
    position: 'fixed',
    /* портал в body: не внутри .menu-page-column (там transform ломал fixed) */
    bottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 32px)',
    maxWidth: 430,
    zIndex: 5000,
    boxSizing: 'border-box',
  },
  cartBtn: {
    width: '100%', padding: '16px 20px',
    borderRadius: '16px', border: 'none',
    backgroundColor: '#000', color: '#fff',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    cursor: 'pointer', fontSize: '15px', fontWeight: '600',
    fontFamily: 'inherit',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  },
  cartCount: {
    backgroundColor: '#fff', color: '#000',
    borderRadius: '50%', width: '26px', height: '26px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '800',
  },
  cartTotal: { fontSize: '15px', fontWeight: '700' },
  minOrderWarn: {
    backgroundColor: '#FFF3E0',
    color: '#E65100',
    padding: '8px 14px',
    borderRadius: '10px',
    fontSize: '13px',
    textAlign: 'center',
    marginBottom: '8px',
    fontWeight: '500',
  },
  formHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '16px', backgroundColor: '#fff',
    borderBottom: '1px solid #F0F0F0', marginBottom: '8px',
  },
  backBtn: {
    padding: '8px 12px', borderRadius: '10px',
    border: '1px solid #E0E0E0', backgroundColor: '#fff',
    cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit',
  },
  formTitle: { fontSize: '17px', fontWeight: '700' },
  section: {
    backgroundColor: '#fff', padding: '16px',
    marginBottom: '8px',
  },
  sectionTitle: { fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#333' },
  label: { fontSize: '13px', color: '#666', marginBottom: '4px' },
  input: {
    width: '100%', padding: '12px 14px',
    borderRadius: '12px', border: '1px solid #E0E0E0',
    fontSize: '15px', boxSizing: 'border-box',
    outline: 'none', fontFamily: 'inherit',
    backgroundColor: '#FAFAFA',
  },
  typeToggle: { display: 'flex', gap: '8px' },
  typeBtn: {
    flex: 1, padding: '10px',
    borderRadius: '10px', border: '1px solid #E0E0E0',
    backgroundColor: '#fff', cursor: 'pointer',
    fontSize: '14px', fontFamily: 'inherit', fontWeight: '500',
  },
  typeBtnActive: {
    backgroundColor: '#000', color: '#fff',
    border: '1px solid #000',
  },
  cartItem: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '14px', padding: '6px 0',
    borderBottom: '1px solid #F5F5F5',
  },
  cartItemTotal: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '16px', fontWeight: '800',
    paddingTop: '10px', marginTop: '4px',
  },
  errorMsg: {
    margin: '0 16px 8px',
    padding: '12px', borderRadius: '10px',
    backgroundColor: '#FFEBEE', color: '#E85D4A',
    fontSize: '14px', textAlign: 'center',
  },
  submitBtn: {
    display: 'block', width: 'calc(100% - 32px)',
    margin: '0 16px 32px',
    padding: '16px', borderRadius: '14px',
    border: 'none', backgroundColor: '#000', color: '#fff',
    fontSize: '16px', fontWeight: '700', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  statusHeader: {
    backgroundColor: '#fff', padding: '32px 16px 24px',
    textAlign: 'center', marginBottom: '8px',
  },
  statusTitle: { fontSize: '22px', fontWeight: '800', marginBottom: '4px' },
  orderId: { fontSize: '14px', color: '#888' },
  statusSteps: {
    backgroundColor: '#fff', padding: '20px 16px',
    marginBottom: '8px',
    display: 'flex', alignItems: 'flex-start',
    overflowX: 'auto',
  },
  statusStep: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', flex: 1, position: 'relative',
  },
  stepIcon: {
    width: '36px', height: '36px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', marginBottom: '6px',
    transition: 'all 0.3s',
  },
  stepLine: {
    position: 'absolute', top: '18px', left: '50%',
    width: '100%', height: '2px', zIndex: 0,
  },
  stepLabel: {
    fontSize: '11px', textAlign: 'center',
    lineHeight: '1.2',
  },
  courierCard: {
    backgroundColor: '#fff', padding: '16px',
    marginBottom: '8px', textAlign: 'center',
  },
  courierTitle: { fontSize: '13px', color: '#888', marginBottom: '4px' },
  courierName: { fontSize: '18px', fontWeight: '700', marginBottom: '8px' },
  courierPhone: {
    display: 'inline-block', padding: '8px 20px',
    borderRadius: '20px', backgroundColor: '#E8F5E9',
    color: '#2E7D32', fontSize: '15px', fontWeight: '600',
    textDecoration: 'none',
  },
  backBtnFull: {
    display: 'block', width: 'calc(100% - 32px)',
    margin: '16px auto',
    padding: '14px', borderRadius: '14px',
    border: '1px solid #E0E0E0', backgroundColor: '#fff',
    fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit',
  },
  empty: {
    padding: '60px 20px', textAlign: 'center', color: '#888', fontSize: '14px',
  },
};

export default function DeliveryPage() {
  return (
    <ThemeProvider>
      <DeliveryPageInner />
    </ThemeProvider>
  );
}
